import React, { useEffect } from 'react';
import { AbercrombysLogo } from './components/AbercrombysLogo';
import { WilliamStreetMap } from './components/WilliamStreetMap';
import { heroVideo, keyStats, propertyDetails } from './data/property';
type GtagWindow = Window & {
  gtag?: (...args: unknown[]) => void;
};
export function App() {
  useEffect(() => {
    // Google Analytics GA4
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-QNMHZ3DCC3';
    document.head.appendChild(script);
    const inlineScript = document.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-QNMHZ3DCC3');
    `;
    document.head.appendChild(inlineScript);
    return () => {
      document.head.removeChild(script);
      document.head.removeChild(inlineScript);
    };
  }, []);
  return <div className="min-h-screen bg-white selection:bg-black selection:text-white overflow-x-hidden" data-id="element-0" data-mp="3ik7eyd5x05p2">
      {/* Navigation */}
      <nav className="absolute top-0 w-full z-50 px-6 md:px-12 pt-[calc(1.25rem+env(safe-area-inset-top))] md:pt-8" data-id="element-1" data-mp="3mkhn9wh6j873">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-4" data-id="element-2" data-mp="3rcsyvwla8ifh">
          <AbercrombysLogo className="h-3 md:h-4 w-auto text-white [filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.35))]" data-id="element-3" data-mp="4wylxj0jajy" />
          <a href="#enquire" onClick={() => {
          const w = window as GtagWindow;
          if (typeof w.gtag === 'function') {
            w.gtag('event', 'enquire_click', {
              property: '14 William Street'
            });
          }
        }} className="eyebrow border border-white/80 text-white px-4 md:px-5 h-11 flex items-center justify-center whitespace-nowrap backdrop-blur-[2px] hover:bg-white hover:text-black active:bg-white active:text-black transition-colors duration-200" data-id="element-4" data-mp="2zmo89g6xiz5t">
            Enquire
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="w-full min-h-[100svh] bg-white flex flex-col" data-id="element-5" data-mp="3udaxmaph6r5r">
        <div className="relative w-full h-[62svh] md:h-[74svh] overflow-hidden bg-gray-light shrink-0" data-id="element-6" data-mp="d4nwrk0ubq3z">
          <video autoPlay loop muted playsInline preload="auto" disablePictureInPicture aria-label="14 William Street, South Yarra" className="hero-drift absolute top-[-5%] left-[-5%] w-[110%] h-[110%] object-cover" data-id="element-7" data-mp="85l0anyxes7m">
            <source src={heroVideo} type="video/mp4" data-id="element-8" data-mp="3qbfv52vt67qe" />
          </video>
          <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-black/35 to-transparent z-10 pointer-events-none" data-id="element-9" data-mp="3lkhfm5in9do7"></div>
          <div className="absolute bottom-0 inset-x-0 h-24 md:h-40 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none" data-id="element-10" data-mp="2xtv6sc0tfpd5"></div>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 md:px-12 pt-6 pb-10 md:py-12 z-20" data-id="element-11" data-mp="2stza6xug707m">
          <div className="max-w-7xl mx-auto w-full flex flex-col md:flex-row justify-between items-start md:items-end gap-8" data-id="element-12" data-mp="1d4hmt3mg185v">
            <div className="flex flex-col items-start max-w-3xl" data-id="element-13" data-mp="ygd2jv7qsokz">
              <p className="eyebrow text-brand-ink bg-brand-wash px-3.5 py-2 mb-5" data-id="element-14" data-mp="ydqyw6nz2t81">
                Pre-Market
              </p>
              <h1 className="font-display font-normal text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl text-black leading-[1.02] tracking-[-0.01em] mb-4 md:mb-5" data-id="element-15" data-mp="1r3r0nwn6fq6o">
                14 William Street, <br className="hidden sm:block" data-id="element-16" data-mp="1i3ukkjeurzn8" />
                <span className="italic text-gray-muted" data-id="element-17" data-mp="1i319j68v2nza">South Yarra.</span>
              </h1>
              <p className="font-body text-gray-dark/80 text-[17px] md:text-lg leading-relaxed max-w-md" data-id="element-18" data-mp="1m3v2ie15usz1">
                A high Victorian home on approximately 900m² of garden, a short
                walk from Toorak Road.
              </p>
            </div>

            <div className="hidden md:flex flex-col items-center gap-3 shrink-0 pb-1" data-id="element-19" data-mp="qxnl5jfbeau0">
              <div className="w-px h-12 bg-black/10 relative overflow-hidden" data-id="element-20" data-mp="m6p3afeoh4on">
                <div className="absolute top-0 left-0 w-full h-full bg-black animate-[scroll_2s_ease-in-out_infinite]" data-id="element-21" data-mp="m4m2r1fwft5o"></div>
              </div>
              <span className="eyebrow text-black [writing-mode:vertical-rl] rotate-180" data-id="element-22" data-mp="c90xmufjacrp">
                Discover
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Key Details */}
      <section className="bg-white border-y border-black/5 relative z-20 px-6 md:px-12" data-id="element-23" data-mp="14xg8d4rmu8x8">
        <dl className="max-w-7xl mx-auto grid grid-cols-4" data-id="element-24" data-mp="zwis4xq6noyd">
          {keyStats.map((stat, i) => <div key={stat.label} className={`py-7 md:py-12 flex flex-col-reverse items-center justify-center text-center gap-2 md:gap-3 ${i !== 0 ? 'border-l border-black/5' : ''}`} data-id="element-25" data-mp="vvgrqdkj56ut" data-mpi={i}>
              <dt className="font-body text-[9px] sm:text-[11px] text-gray-muted tracking-[0.16em] sm:tracking-[0.2em] uppercase whitespace-nowrap" data-id="element-26" data-mp="3gok7rvi5uvtd">
                {stat.label}
              </dt>
              <dd className="font-display text-[1.75rem] md:text-4xl text-black leading-none" data-id="element-27" data-mp="206a5rvb5fv2f">
                {stat.value}
              </dd>
            </div>)}
        </dl>
      </section>

      {/* The Property */}
      <section className="bg-offWhite py-20 md:py-32 px-6 md:px-12" data-id="element-28" data-mp="2euws4gz71n35">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-24" data-id="element-29" data-mp="2eumxuh748trn">
          <div className="md:col-span-5" data-id="element-30" data-mp="2js6f7pz41mnt">
            <SectionLabel data-id="element-31" data-mp="2nvuocd3e4qb6">The Property</SectionLabel>
            <h2 className="font-display text-[2.25rem] md:text-5xl text-black leading-[1.08]" data-id="element-32" data-mp="2nwe53qhb18nm">
              Invergowrie, <br data-id="element-33" data-mp="2sslddpx6aw8q" />
              <span className="italic text-gray-muted" data-id="element-34" data-mp="thlbty3w8tyu">c. 1885.</span>
            </h2>
          </div>
          <div className="md:col-span-7 flex items-end" data-id="element-35" data-mp="ygoj7p7cjc9z">
            <p className="font-body text-gray-dark text-[17px] md:text-xl leading-[1.75] md:leading-[1.8] md:pl-12 md:border-l border-black/10" data-id="element-36" data-mp="yh6lp3klqmqc">
              The original detail is intact. Polychrome brickwork, ornate
              plasterwork, marble fireplaces and high ceilings throughout.
            </p>
          </div>
        </div>
      </section>

      {/* The Detail */}
      <section className="features-wash py-20 md:py-32 px-6 md:px-12" data-id="element-37" data-mp="25s4c6plc93u7">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-24" data-id="element-38" data-mp="2kq6iv8no25fo">
          <div className="md:col-span-5" data-id="element-39" data-mp="2frlgb2kxz9o2">
            <div className="md:sticky md:top-32" data-id="element-40" data-mp="2fqk610br2r7e">
              <SectionLabel tone="brand" data-id="element-41" data-mp="2tiqiabbs8e1k">The Detail</SectionLabel>
              <h2 className="font-display text-[2.25rem] md:text-5xl text-black leading-[1.08]" data-id="element-42" data-mp="2thymp0i6qgba">
                Intact <br data-id="element-43" data-mp="2pj9edmc07oyi" />
                <span className="italic text-brand-ink" data-id="element-44" data-mp="33gxnjwwpl7nq">Throughout.</span>
              </h2>
            </div>
          </div>
          <div className="md:col-span-7" data-id="element-45" data-mp="2yicv4jnp7s9u">
            <dl className="flex flex-col border-t border-brand-ink/15" data-id="element-46" data-mp="2yjo2etgcri5p">
              {propertyDetails.map(item => <div key={item.label} className="py-6 md:py-8 border-b border-brand-ink/15 grid grid-cols-1 md:grid-cols-[10rem_1fr] gap-1.5 md:gap-8" data-id="element-47" data-mp="3i1sbvc5hr738">
                  <dt className="font-display italic text-xl md:text-2xl text-brand-ink leading-snug" data-id="element-48" data-mp="3d4jrd4wveqyt">
                    {item.label}
                  </dt>
                  <dd className="font-body text-gray-dark text-base md:text-lg leading-relaxed md:pt-0.5" data-id="element-49" data-mp="2zbkd168tr2su">
                    {item.text}
                  </dd>
                </div>)}
            </dl>
          </div>
        </div>
      </section>

      {/* The Setting */}
      <WilliamStreetMap data-id="element-50" data-mp="2uf3ls36bw8bj" />

      {/* Agent */}
      <section id="enquire" className="agent-wash py-20 md:py-32 px-6 md:px-12 scroll-mt-4" data-id="element-51" data-mp="2ubpj0ttr5x3k">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-24 items-center" data-id="element-52" data-mp="2kfmj7umdiajs">
          <div className="md:col-span-5" data-id="element-53" data-mp="30vx3juoqhvkq">
            <div className="relative w-full max-w-[240px] md:max-w-sm mx-auto" data-id="element-54" data-mp="30uvgssycvkn6">
              <div className="aspect-[4/5] md:aspect-[3/4] w-full bg-white relative z-10 overflow-hidden" data-id="element-55" data-mp="2vxbz1j2g8pqh">
                <img src="https://cdn.magicpatterns.com/uploads/buweXkysMhpJnTz6ubbH6B/Luca-Pignalosa-scaled.webp" alt="Luca Pignalosa" loading="lazy" decoding="async" className="w-full h-full object-cover object-top" data-id="element-56" data-mp="2qvwj25f4p9ey" />
              </div>
              <div className="absolute top-5 left-5 w-full h-full border border-brand-ink/25 z-0 hidden md:block" data-id="element-57" data-mp="286puw2vkfplr"></div>
            </div>
          </div>

          <div className="md:col-span-7 flex flex-col items-center md:items-start text-center md:text-left" data-id="element-58" data-mp="238y5pgmb2hl9">
            <SectionLabel tone="brand" centered data-id="element-59" data-mp="1y80igpmfuxcw">
              Private Inspections
            </SectionLabel>
            <h3 className="font-display text-[2.25rem] md:text-5xl text-black leading-[1.08] mb-4" data-id="element-60" data-mp="1u91kjoz4k0uy">
              Luca Pignalosa
            </h3>
            <AbercrombysLogo className="h-3 md:h-3.5 w-auto text-brand-ink mb-10" data-id="element-61" data-mp="dpyeivkjgopk" />

            <div className="w-full max-w-sm md:max-w-md flex flex-col border-t border-brand-ink/15" data-id="element-62" data-mp="ipu9y6f677ti">
              <ContactRow href="tel:+61497017146" label="Call" data-id="element-63" data-mp="nofa65uf9rhy">
                0497 017 146
              </ContactRow>
              <ContactRow href="mailto:lucap@abercrombys.com.au" label="Email" data-id="element-64" data-mp="3m72r0o22q3n6">
                lucap@abercrombys.com.au
              </ContactRow>
            </div>

            <p className="font-body italic text-base text-brand-ink mt-8" data-id="element-65" data-mp="3w4h72mk34qey">
              Private inspections by appointment.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white pt-10 md:pt-14 pb-[calc(2.5rem+env(safe-area-inset-bottom))] md:pb-14 px-6 md:px-12 border-t border-black/5" data-id="element-66" data-mp="33g35xxxp9dz6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-67" data-mp="38dlg1rngdz8r">
          <p className="eyebrow text-gray-muted text-center md:text-left leading-relaxed" data-id="element-68" data-mp="2f6lnt8ei8wuu">
            14 William Street, South Yarra
          </p>
          <p className="eyebrow text-gray-muted/60" data-id="element-69" data-mp="2p5koo4t13o7a">© 2026 Abercrombys</p>
        </div>
      </footer>

      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes scroll {
            0% { transform: translateY(-100%); }
            50% { transform: translateY(0); }
            100% { transform: translateY(100%); }
          }
        `
    }} data-id="element-70" data-mp="2u44b06bjb206" />
    </div>;
}
type SectionLabelProps = {
  children: React.ReactNode;
  tone?: 'ink' | 'brand';
  centered?: boolean;
};
function SectionLabel({
  children,
  tone = 'ink',
  centered
}: SectionLabelProps) {
  const color = tone === 'brand' ? 'text-brand-ink' : 'text-black';
  const rule = tone === 'brand' ? 'bg-brand-ink' : 'bg-black';
  return <div className={`flex items-center gap-4 mb-5 md:mb-8 ${centered ? 'justify-center md:justify-start' : ''}`} data-id="element-71" data-mp="1io7b5n25qxo6">
      <span className={`w-8 h-px ${rule}`} aria-hidden="true" data-id="element-72" data-mp="3vypjflmxgcga"></span>
      <span className={`eyebrow ${color}`} data-id="element-73" data-mp="3ryyl8wv2mu1q">{children}</span>
    </div>;
}
type ContactRowProps = {
  href: string;
  label: string;
  children: React.ReactNode;
};
function ContactRow({
  href,
  label,
  children
}: ContactRowProps) {
  return <a href={href} className="group flex items-center justify-between gap-4 min-h-[56px] py-3 border-b border-brand-ink/15 text-left active:bg-white/40 transition-colors duration-200" data-id="element-74" data-mp="3i1twqqdr6mhr">
      <span className="eyebrow text-brand-ink/70 w-14 shrink-0" data-id="element-75" data-mp="387tle6rvol0l">{label}</span>
      <span className="font-body text-[17px] md:text-lg text-black flex-1 truncate group-hover:text-brand-ink transition-colors duration-200" data-id="element-76" data-mp="348uggm65co95">
        {children}
      </span>
      <span className="text-brand-ink/60 group-hover:translate-x-1 transition-transform duration-200" aria-hidden="true" data-id="element-77" data-mp="2u93mclli0b9l">
        →
      </span>
    </a>;
}